import { useEffect, useMemo, useState } from 'react';
import type { ScheduleActivityItem } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_activity.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useGetCalendarEventInfo } from '@frontend/schedule-helpers';
import { DATETIME_FORMATS } from '../../constants';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../stores/schedule-appointment-info-panel.store';
import { openSchedulePulseRequest } from '../../stores/schedule-pulse.store';
import { useScheduleActionsContextValue } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-actions-context-value';
import { useScheduleRequestModalContextValue } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-schedule-request-modal-context-value';
import { convertScheduleRequestV3ToV2 } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useGetScheduleRequest';
import { ACTIVITY_ROW_CLICK_ALLOWED_STATUSES } from './constants';

type SelectedAppointmentRequest = {
  eventId: string;
  locationId: string;
  referenceId: string;
  startDate: string;
};

const useHandleAppointmentRowClick = () => {
  const [selectedAppointmentRequest, setSelectedAppointmentRequest] = useState<SelectedAppointmentRequest>();

  const { setSelectedAppointmentId, selectedAppointmentId, setSelectedAppointment } =
    useScheduleAppointmentInfoPanelShallowStore(
      'setSelectedAppointmentId',
      'selectedAppointmentId',
      'setSelectedAppointment'
    );

  const { eventInfo: selectedCalendarEvent } = useGetCalendarEventInfo({
    eventId: selectedAppointmentRequest?.eventId ?? '',
    locationId: selectedAppointmentRequest?.locationId ?? '',
    startDate: selectedAppointmentRequest?.startDate,
    referenceId: selectedAppointmentRequest?.referenceId,
    enabled: !!selectedAppointmentRequest,
  });

  useEffect(() => {
    if (
      !selectedCalendarEvent ||
      !selectedAppointmentRequest ||
      selectedAppointmentId !== selectedAppointmentRequest.eventId
    ) {
      return;
    }
    setSelectedAppointment(selectedCalendarEvent);
  }, [selectedAppointmentId, selectedAppointmentRequest, selectedCalendarEvent, setSelectedAppointment]);

  useEffect(() => {
    if (!selectedAppointmentId) {
      setSelectedAppointmentRequest(undefined);
    }
  }, [selectedAppointmentId]);

  const handleAppointmentRowClick = (row: ScheduleActivityItem) => {
    const { appointmentId, appointmentTime, calendarEventId, locationId } = row;
    if (!appointmentId || !appointmentTime || !calendarEventId || !locationId) return;

    setSelectedAppointmentRequest({
      locationId,
      eventId: calendarEventId,
      referenceId: appointmentId,
      startDate: dayjs(appointmentTime).format(DATETIME_FORMATS.ISO_DATE),
    });

    setSelectedAppointmentId(calendarEventId);
  };

  return handleAppointmentRowClick;
};

const useHandleRequestRowClick = () => {
  const [selectedBookingSubmissionId, setSelectedBookingSubmissionId] = useState('');
  const [isRequestApprovalFormDisabled, setIsRequestApprovalFormDisabled] = useState(false);

  const scheduleActionsContextValue = useScheduleActionsContextValue();

  const { selectedScheduleRequest, setSelectedScheduleRequest, scheduleRequestModalContextValue } =
    useScheduleRequestModalContextValue({
      refetchScheduleRequestList: () => {},
      isScheduleRequestFormDisabled: isRequestApprovalFormDisabled,
    });

  const { modalProps, openModal, closeModal } = scheduleRequestModalContextValue.scheduleRequestModalProps;

  const bookingSubmissionQuery = SchedulerV3.Queries.useGetBookingSubmissionQuery({
    request: { submissionId: selectedBookingSubmissionId },
    options: {
      enabled: !!selectedBookingSubmissionId,
      select: (response) =>
        response.bookingSubmission ? convertScheduleRequestV3ToV2(response.bookingSubmission) : undefined,
    },
  });

  // When the booking submission query returns, update the selected schedule request state and close the modal if necessary
  useEffect(() => {
    if (!selectedBookingSubmissionId) return;

    if (bookingSubmissionQuery.data?.id === selectedBookingSubmissionId) {
      setSelectedScheduleRequest(bookingSubmissionQuery.data);
      return;
    }

    if (bookingSubmissionQuery.isError || (bookingSubmissionQuery.isFetched && !bookingSubmissionQuery.data)) {
      setSelectedScheduleRequest(null);
      setSelectedBookingSubmissionId('');
      closeModal();
    }
  }, [
    bookingSubmissionQuery.data,
    bookingSubmissionQuery.isError,
    bookingSubmissionQuery.isFetched,
    selectedBookingSubmissionId,
    setSelectedScheduleRequest,
    closeModal,
  ]);

  // Reset the selected booking submission ID when the modal is closed
  useEffect(() => {
    if (!modalProps.show && selectedBookingSubmissionId) {
      setSelectedBookingSubmissionId('');
      setIsRequestApprovalFormDisabled(false);
    }
  }, [modalProps.show, selectedBookingSubmissionId]);

  const handleRequestRowClick = (row: ScheduleActivityItem) => {
    if (!row.bookingSubmissionId) return;

    const isAiRequest = row.activitySourceType === 'AI_VOICE' || row.activitySourceType === 'AI_TEXT';

    if (isAiRequest) {
      const hasRowClickAllowedStatus = ACTIVITY_ROW_CLICK_ALLOWED_STATUSES.includes(row.currentStatus ?? '');
      if (!hasRowClickAllowedStatus || !row.locationId) return;

      // Close request review modal and open the schedule pulse request details
      closeModal();
      setSelectedBookingSubmissionId('');
      openSchedulePulseRequest(row.locationId, row.bookingSubmissionId);
    } else {
      // For already accepted requests, disable the request approval form and open request review modal
      setIsRequestApprovalFormDisabled(row.currentStatus === 'ACCEPTED');
      setSelectedBookingSubmissionId(row.bookingSubmissionId);
      openModal();
    }
  };

  const requestModalDetails = useMemo(() => {
    return {
      selectedBookingSubmissionId,
      selectedScheduleRequest,
      isLoadingScheduleRequest: !!bookingSubmissionQuery.isLoading,
      scheduleRequestModalContextValue,
      scheduleActionsContextValue,
      showModal: modalProps.show,
    };
  }, [
    selectedBookingSubmissionId,
    selectedScheduleRequest,
    bookingSubmissionQuery.isLoading,
    scheduleRequestModalContextValue,
    scheduleActionsContextValue,
    modalProps.show,
  ]);

  return {
    handleRequestRowClick,
    requestModalDetails,
  };
};

export const useHandleActivityRowClick = (trackRowClicked: (row: ScheduleActivityItem) => void) => {
  const handleAppointmentRowClick = useHandleAppointmentRowClick();
  const { handleRequestRowClick, requestModalDetails } = useHandleRequestRowClick();

  const handleRowClick = (row: ScheduleActivityItem) => {
    if (row.activityType === 'REQUESTED') {
      handleRequestRowClick(row);
    } else {
      handleAppointmentRowClick(row);
    }

    trackRowClicked(row);
  };

  return { handleRowClick, requestModalDetails };
};
